"use client"

import { useEffect, useMemo, useState } from "react"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"
import { Activity, LineChart as LineChartIcon } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { fetchAutoTradeSettings, fetchPairSeries, type PairSeriesPoint, type PairSeriesResponse } from "@/lib/agent-force"

function formatAxisTime(iso: string) {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso.slice(11, 16) : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
}

function priceDecimals(pair: string) {
  return pair.toUpperCase().includes("JPY") ? 2 : 4
}

function pipSize(pair: string) {
  return pair.toUpperCase().includes("JPY") ? 0.01 : 0.0001
}

export function CurrencyPositions({ selectedPair }: { selectedPair: string | null }) {
  const [series, setSeries] = useState<PairSeriesResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [adminTimeframe, setAdminTimeframe] = useState<string>("M15")

  useEffect(() => {
    let cancelled = false
    fetchAutoTradeSettings()
      .then((cfg) => {
        if (!cancelled) setAdminTimeframe(String(cfg.timeframe || "M15").toUpperCase())
      })
      .catch(() => {
        if (!cancelled) setAdminTimeframe("M15")
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!selectedPair) {
      setSeries(null)
      setFetchError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setFetchError(null)
    fetchPairSeries(selectedPair, 5, adminTimeframe)
      .then((data) => {
        if (!cancelled) setSeries(data)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setSeries(null)
          setFetchError(e instanceof Error ? e.message : "Failed to load series")
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedPair, adminTimeframe])

  const chartData = useMemo(() => {
    const pts = series?.points ?? []
    return pts.map((p: PairSeriesPoint) => ({
      ...p,
      label: formatAxisTime(p.time),
    }))
  }, [series])

  const yDomain = useMemo((): [number, number] => {
    const closes = chartData.map((d) => d.close).filter((c) => Number.isFinite(c))
    if (closes.length === 0) return [0, 1]
    const lo = Math.min(...closes)
    const hi = Math.max(...closes)
    const pad = Math.max((hi - lo) * 0.08, hi * 0.0002, 1e-6)
    return [lo - pad, hi + pad]
  }, [chartData])

  const dec = selectedPair ? priceDecimals(selectedPair) : 4
  const pips = selectedPair ? pipSize(selectedPair) : 0.0001
  const volatility = useMemo(() => {
    if (chartData.length < 2) {
      return { realizedPct: null as number | null, avgRangePips: null as number | null }
    }
    const closes = chartData.map((d) => d.close).filter((c) => Number.isFinite(c))
    const returns: number[] = []
    for (let i = 1; i < closes.length; i += 1) {
      const prev = closes[i - 1]
      const cur = closes[i]
      if (prev > 0 && Number.isFinite(cur)) returns.push(Math.log(cur / prev))
    }
    const mean = returns.length > 0 ? returns.reduce((a, b) => a + b, 0) / returns.length : 0
    const variance =
      returns.length > 1 ? returns.reduce((sum, r) => sum + (r - mean) ** 2, 0) / (returns.length - 1) : 0
    const realizedPct = Math.sqrt(Math.max(variance, 0)) * 100

    const ranges = chartData
      .map((d) => d.high - d.low)
      .filter((v) => Number.isFinite(v) && v >= 0)
    const avgRange = ranges.length > 0 ? ranges.reduce((a, b) => a + b, 0) / ranges.length : 0
    const avgRangePips = avgRange / pips

    return { realizedPct, avgRangePips }
  }, [chartData, pips])
  const subtitle =
    series?.resolved_symbol && series.requested_symbol
      ? `${series.requested_symbol} → ${series.resolved_symbol} · ${series.timeframe} · ~${series.hours_requested}h window`
      : "Select a row in Market Overview"

  return (
    <Card className="min-w-0 w-full bg-card border-border">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="space-y-1">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <LineChartIcon className="h-5 w-5 text-primary" />
            Pair timeline
          </CardTitle>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        {selectedPair && (
          <Badge variant="outline" className="font-mono text-xs shrink-0">
            {selectedPair}
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {!selectedPair && (
          <div className="flex flex-col items-center justify-center gap-2 py-16 px-4 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
            <Activity className="h-8 w-8 opacity-40" />
            <p>
              Click a currency pair in <span className="text-foreground font-medium">Market Overview</span> to load the last ~5 hours of{" "}
              {adminTimeframe} closes from MetaTrader.
            </p>
          </div>
        )}
        {selectedPair && loading && (
          <div className="h-[280px] flex items-center justify-center text-sm text-muted-foreground">Loading chart…</div>
        )}
        {selectedPair && !loading && fetchError && (
          <div className="h-[280px] flex items-center justify-center text-sm text-negative px-4 text-center">{fetchError}</div>
        )}
        {selectedPair && !loading && !fetchError && series?.status === "error" && (
          <div className="h-[280px] flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground px-4 text-center">
            <p className="text-negative">{series.message || "Could not load OHLCV."}</p>
            <p className="text-xs">Check MT5 connection and that the symbol exists on your broker.</p>
          </div>
        )}
        {selectedPair && !loading && !fetchError && series?.status === "ok" && chartData.length === 0 && (
          <div className="h-[280px] flex items-center justify-center text-sm text-muted-foreground">No bars returned for this window.</div>
        )}
        {selectedPair && !loading && !fetchError && series?.status === "ok" && chartData.length > 0 && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="font-mono text-xs">
                Volatility {volatility.realizedPct != null ? `${volatility.realizedPct.toFixed(2)}%` : "n/a"}
              </Badge>
              <Badge variant="outline" className="font-mono text-xs">
                Avg range {volatility.avgRangePips != null ? `${volatility.avgRangePips.toFixed(1)} pips` : "n/a"}
              </Badge>
            </div>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.25 0.005 260)" vertical={false} />
                <XAxis dataKey="label" stroke="oklch(0.60 0 0)" fontSize={10} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis
                  domain={yDomain}
                  stroke="oklch(0.60 0 0)"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  width={56}
                  tickFormatter={(v: number) => v.toFixed(dec)}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "oklch(0.14 0.005 260)",
                    border: "1px solid oklch(0.25 0.005 260)",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                  labelFormatter={(_, items) => {
                    const t = (items?.[0]?.payload as PairSeriesPoint | undefined)?.time
                    if (!t) return ""
                    const d = new Date(t)
                    return Number.isNaN(d.getTime()) ? String(t) : d.toLocaleString()
                  }}
                  formatter={(value: number) => [value.toFixed(dec), "Close"]}
                />
                <Line type="monotone" dataKey="close" stroke="oklch(0.70 0.18 250)" strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
